import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject } from '@angular/core';
import { App } from '@df/core/app';
import { AbIfService } from '@df/module-ab/ab-if.service';
import { ImageModule } from '@df/module-image/image.module';
import { NgInject } from '@df/ng/ng-inject';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { ColorOptions, RatioOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { PageHeaderComponent } from '../../../../layout/page/header/page-header.component';
import { BehaviorSubject } from 'rxjs';
import { SwiperModule } from 'swiper/angular';
export interface IPageComponentNotificationBarBlockComponentData extends NICmsContentComponent.IData {
    elements: NotificationBarElements[];
    elements_b: NotificationBarElements[];
    experimentConfiguration?: string;
    banner_image?: string;
    banner_image_ratio?: string;
    banner_background_color?: string;
    elements_background_color?: string;
    slider_animation_delay?: number;
    slider_animation_speed?: number;
}
interface NotificationBarElements {
    text?: string;
    cta_copy?: string;
    cta_link?: string;
    font_color?: string;
    background_color?: string;
    banner_before_image_text?: string;
    banner_after_image_text?: string;
    icon?: 'experience' | 'express' | 'tag' | 'clickcollect' | 'tree';
}

const elementRepeaterFields = [
    {
        type: NICmsContentField.EFieldType.text,
        key: 'text',
        title: 'Text'
    },
    {
        type: NICmsContentField.EFieldType.text,
        key: 'cta_copy',
        title: 'CTA copy'
    },
    {
        type: NICmsContentField.EFieldType.text,
        key: 'cta_link',
        title: 'CTA link'
    },
    <NICmsContentField.IColorPickerField>{
        key: 'font_color',
        type: NICmsContentField.EFieldType.colorPicker,
        title: 'Font Colour',
        options: ColorOptions,
        colorPreview: true
    },
    <NICmsContentField.IColorPickerField>{
        key: 'background_color',
        type: NICmsContentField.EFieldType.colorPicker,
        title: 'Background Colour',
        options: ColorOptions,
        colorPreview: true
    },
    <NICmsContentField.IChoiceField>{
        type: NICmsContentField.EFieldType.choice,
        key: 'icon',
        title: 'Icon',
        options: [
            { key: 'experience', title: 'Experience' },
            { key: 'express', title: 'Express' },
            { key: 'tag', title: 'Tag' },
            { key: 'clickcollect', title: 'Click and collect' },
            { key: 'tree', title: 'Tree' },
            { key: 'four', title: 'Four' },
            { key: 'four-circle', title: 'Four Circle' },
            { key: 'autumn', title: 'Autumn' },
            { key: 'clock', title: 'Clock' },
            { key: 'flower', title: 'Flower' },
            { key: 'flower-2', title: 'Flower 2' },
            { key: 'wishlist', title: 'Wishlist' },
            { key: 'wishlist-fill', title: 'Wishlist Fill' },
            { key: 'leaf', title: 'Leaf' },
            { key: 'email', title: 'Email' },
            { key: 'pound', title: 'Pound' },
            { key: 'snowflake', title: 'Snowflake' },
            { key: 'star', title: 'Star' },
            { key: 'star-empty', title: 'Star Empty' },
            { key: 'sun', title: 'Sun' },
            { key: 'location', title: 'Location' }
        ]
    }
];
@Component({
    selector: 'page-component-notification-bar',
    templateUrl: 'notification-bar.html',
    styleUrls: ['./notification-bar.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ICmsClientModule, CommonModule, SwiperModule, ImageModule, HrefComponent, CarouselModule]
})
@ICmsComponent<PageComponentNotificationBarComponent>({
    id: 'notification-bar-carousel',
    block: {
        url: 'notification-bar-carousel',
        scope: 'website',
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Notfication Bar Layout',
    fields: [
        <NICmsContentField.IColorPickerField>{
            key: 'banner_background_color',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Banner Background Color'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'banner_before_image_text',
            title: 'Banner Text Before Image'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'banner_after_image_text',
            title: 'Banner Text After Image'
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'banner_image',
            title: 'Banner Image'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'banner_image_ratio',
            title: 'Banner image ratio',
            options: RatioOptions,
            default: 'ratio-10-1'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'experimentConfiguration',
            title: 'Experiment Configuration',
            options: [
                { key: undefined, title: `Experiment Enabled (id: trustSignals)` },
                { key: 'A', title: 'A Displayed (Experiment Disabled)' },
                { key: 'B', title: 'B Displayed (Experiment Disabled)' }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            key: 'elements',
            title: 'Elements (A)',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            defaultSize: 1,
            fields: elementRepeaterFields
        },
        <NICmsContentField.IRepeaterField>{
            key: 'elements_b',
            title: 'Elements (B)',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            defaultSize: 1,
            fields: elementRepeaterFields
        },
        <NICmsContentField.IColorPickerField>{
            key: 'elements_background_color',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'All Elements Background Color',
            description: 'Only for desktop'
        },
        {
            type: NICmsContentField.EFieldType.number,
            key: 'slider_animation_delay',
            title: 'Slider Animation Delay',
            default: 4000
        },
        {
            type: NICmsContentField.EFieldType.number,
            key: 'slider_animation_speed',
            title: 'Slider Animation Speed',
            default: 2000
        }
    ]
})
export class PageComponentNotificationBarComponent extends UiComponent implements NICmsContentComponent.IComponent {
    constructor(
        private elementRef: ElementRef
    ) {
        super();
        this.updateView();
        this.app.client.obs.resize.subscribe(e => {
            this.updateView();
        });
    }
    @ICmsContent({
        type: 'blockContent'
    })
    content!: IPageComponentNotificationBarBlockComponentData;

    private updateView(): void {
        this.showCarousel = this.app.client.width < 1360;
        this.detectChanges();
    }

    private experimentId = 'trustSignals';

    private pageHeaderComponent = inject(PageHeaderComponent);

    showCarousel: boolean = false;

    override ngOnInit(): void {
        super.ngOnInit();
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, true);
        // Subscribe to experiment status changes if not disabled
        (!this.content?.experimentConfiguration || this.app.client.isCms) &&
            this.subscriptions.add(this.app.ab.getSubject(this.experimentId).subscribe(() => this.detectChanges()));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, false);
    }
    hasContent$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    isHidden = false;

    @NgInject(AbIfService)
    abIfService!: AbIfService;

    @NgInject(DOCUMENT)
    private document!: Document;

    @NgInject(App)
    app!: App;

    hideNotificationBar(): void {
        this.isHidden = true;
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, false);
        this.detectChanges();
    }

    override detectChanges() {
        super.detectChanges();
        const hasContent = !!this.content;
        if (this.hasContent$.value !== hasContent) {
            this.hasContent$.next(hasContent);
        }
    }

    get experimentStatus(): string {
        return this.app.ab.getVariant(this.experimentId);
    }
    get elements(): NotificationBarElements[] {
        const config = this.content?.experimentConfiguration;
        return (config ? config === 'B' : this.experimentStatus === 'B') ? this.content?.elements_b : this.content?.elements;
    }
}
